// BackupRestorePanel — the Backup / Restore tab. For a backup-role operator
// it's their entire app; admins also get it (and will later see scheduled-job
// backups unioned in). Lists stored server-side backups + drives:
//   New backup  → pick connection+database → BackupDialog (server mode)
//   Restore     → pick target connection+database → ServerRestoreDialog
//   Delete      → (allow_delete_backup) confirm → delete
//
// Easy vs advanced dialogs follow the user's easy_backup_restore flag; holding
// Shift when clicking New backup / Restore forces the simplified flow.
import { createSignal, createResource, onMount, onCleanup, For, Show } from 'solid-js';
import { currentUser } from '../lib/auth-store.js';
import { listServerBackups, bulkDeleteServerBackups, downloadServerBackup, pruneServerBackups, listServerOperations, getServerBackupStorage } from '../lib/serverbackups.js';
import { resolveRestore } from '../lib/restore.js';
import { formatBytes } from '../lib/format.js';
import { toast } from '../lib/toast.js';
import ConnDbPickerModal from '../components/ConnDbPickerModal.jsx';
import BackupDialog from '../components/BackupDialog.jsx';
import ServerRestoreDialog from '../components/ServerRestoreDialog.jsx';
import Modal from '../components/Modal.jsx';

const TERMINAL = { done: 1, error: 1, aborted: 1 };

const fmtTime = (unixSec) => {
  if (!unixSec) return '';
  try { return new Date(unixSec * 1000).toLocaleString(); } catch { return String(unixSec); }
};

export default function BackupRestorePanel() {
  const isAdmin = () => currentUser()?.role === 'admin';
  const userEasy = () => !!currentUser()?.easyBackupRestore;
  const canDelete = () => isAdmin() || !!currentUser()?.allowDeleteBackup;

  const [backups, { refetch }] = createResource(listServerBackups);
  const [storage, { refetch: refetchStorage }] = createResource(getServerBackupStorage);

  // ── Multi-select (checkboxes) ───────────────────────────────────────────────
  // The selection is a set of backup ids. Restore acts on a SINGLE backup, so
  // it's only enabled when exactly one is selected; delete + download work on the
  // selection (bulk delete; download is per-row / single).
  const [selectedIds, setSelectedIds] = createSignal(new Set());
  const isSel = (id) => selectedIds().has(id);
  const toggleSel = (id) => setSelectedIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setSelMany = (ids, on) => setSelectedIds((s) => { const n = new Set(s); for (const id of ids) on ? n.add(id) : n.delete(id); return n; });
  const clearSel = () => setSelectedIds(new Set());
  const selectedRecords = () => (backups() || []).filter((b) => selectedIds().has(b.id));
  const selectedOne = () => { const r = selectedRecords(); return r.length === 1 ? r[0] : null; };

  // Bytes → GB (binary GiB, matching what the OS reports as "GB"), 1 decimal.
  const gb = (b) => (Number(b || 0) / (1024 ** 3)).toFixed(1);

  // ── Sort / filter / search (all client-side over the loaded list) ───────────
  const [sortKey, setSortKey] = createSignal('createdAt'); // default: Taken
  const [sortDir, setSortDir] = createSignal('desc');      // …descending
  const [search, setSearch]   = createSignal('');
  const [fltDb, setFltDb]     = createSignal('');
  const [fltConn, setFltConn] = createSignal('');
  const [fltUser, setFltUser] = createSignal('');
  const [fltSource, setFltSource] = createSignal(''); // '' all · '__manual__' · a schedule (job) name
  const NUMERIC = { sizeBytes: 1, createdAt: 1 };
  const MANUAL = '__manual__';

  const toggleSort = (key) => {
    if (sortKey() === key) { setSortDir(sortDir() === 'asc' ? 'desc' : 'asc'); return; }
    setSortKey(key);
    setSortDir(NUMERIC[key] ? 'desc' : 'asc'); // numbers default high→low, text A→Z
  };

  // Distinct, sorted values for a filter dropdown.
  const distinct = (pick) => {
    const s = new Set();
    for (const b of backups() || []) { const v = pick(b); if (v) s.add(v); }
    return [...s].sort((a, b) => a.localeCompare(b));
  };
  const dbOptions   = () => distinct((b) => b.database);
  const connOptions = () => distinct((b) => b.connectionName);
  const userOptions = () => distinct((b) => b.createdByName);
  // Schedule names among scheduled backups (manual rows carry no job name).
  const scheduleOptions = () => distinct((b) => b.kind === 'scheduled' ? b.jobName : '');
  const filtersActive = () => !!(search().trim() || fltDb() || fltConn() || fltUser() || fltSource());
  const clearFilters = () => { setSearch(''); setFltDb(''); setFltConn(''); setFltUser(''); setFltSource(''); };

  // The visible rows: search + dropdown filters, then sort.
  const displayed = () => {
    let list = (backups() || []).slice();
    const q = search().trim().toLowerCase();
    if (q) list = list.filter((b) =>
      [b.database, b.connectionName, b.createdByName, b.note, b.jobName]
        .some((v) => (v || '').toLowerCase().includes(q)));
    if (fltDb())   list = list.filter((b) => b.database === fltDb());
    if (fltConn()) list = list.filter((b) => b.connectionName === fltConn());
    if (fltUser()) list = list.filter((b) => b.createdByName === fltUser());
    if (fltSource() === MANUAL) list = list.filter((b) => b.kind !== 'scheduled');
    else if (fltSource()) list = list.filter((b) => b.kind === 'scheduled' && b.jobName === fltSource());
    const key = sortKey(), dir = sortDir() === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (NUMERIC[key]) return ((Number(a[key]) || 0) - (Number(b[key]) || 0)) * dir;
      const av = (a[key] == null ? '' : String(a[key])).toLowerCase();
      const bv = (b[key] == null ? '' : String(b[key])).toLowerCase();
      return av < bv ? -dir : av > bv ? dir : 0;
    });
    return list;
  };

  // ── Group scheduled backups by their RUN ────────────────────────────────────
  // A scheduled backup job run dumps N databases → N rows sharing one run_id;
  // collapse each run into a single expandable entry. Manual backups and legacy
  // scheduled rows (run_id=0, taken before run_id existed) stay standalone.
  const [expanded, setExpanded] = createSignal(new Set()); // run_ids currently open
  const toggleExpand = (runId) => setExpanded((s) => {
    const n = new Set(s); n.has(runId) ? n.delete(runId) : n.add(runId); return n;
  });

  // Top-level entries over the filtered+sorted list: { type:'single', backup } or
  // { type:'group', runId, items, …aggregates }. Re-sorted by the active column
  // using each group's representative (summed size, latest time, etc.).
  const grouped = () => {
    const byRun = new Map();
    const entries = [];
    for (const b of displayed()) {
      if (b.kind === 'scheduled' && b.runId > 0) {
        let g = byRun.get(b.runId);
        if (!g) { g = { type: 'group', runId: b.runId, items: [] }; byRun.set(b.runId, g); entries.push(g); }
        g.items.push(b);
      } else {
        entries.push({ type: 'single', backup: b });
      }
    }
    for (const e of entries) {
      if (e.type !== 'group') continue;
      const it = e.items;
      e.jobName = it[0].jobName;
      e.createdAt = Math.max(...it.map((x) => x.createdAt || 0));
      e.sizeBytes = it.reduce((n, x) => n + (x.sizeBytes || 0), 0);
      e.createdByName = it[0].createdByName;
      const conns = [...new Set(it.map((x) => x.connectionName).filter(Boolean))];
      e.connectionName = conns.length <= 1 ? (conns[0] || '') : `${conns.length} connections`;
      e.database = `${it.length} database${it.length === 1 ? '' : 's'}`;
      const bad = it.filter((x) => x.status && x.status !== 'ok').length;
      e.status = bad === 0 ? 'ok' : bad === it.length ? 'error' : 'partial';
      e.note = '';
    }
    const key = sortKey(), dir = sortDir() === 'asc' ? 1 : -1;
    const val = (e) => (e.type === 'group' ? e[key] : e.backup[key]);
    entries.sort((a, b) => {
      if (NUMERIC[key]) return ((Number(val(a)) || 0) - (Number(val(b)) || 0)) * dir;
      const av = (val(a) == null ? '' : String(val(a))).toLowerCase();
      const bv = (val(b) == null ? '' : String(val(b))).toLowerCase();
      return av < bv ? -dir : av > bv ? dir : 0;
    });
    return entries;
  };

  // One data row for a backup — used for standalone rows and group children.
  const BackupRowTr = (b, child) => (
    <tr classList={{ 'row-selected': isSel(b.id), 'bk-child-row': child }}
        style="cursor:pointer;"
        onClick={() => toggleSel(b.id)}>
      <td class="bk-ck" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={isSel(b.id)} onChange={() => toggleSel(b.id)} />
      </td>
      <td><span class="mono" style={child ? 'padding-left:20px;' : ''}>{child ? '↳ ' : ''}{b.database}</span></td>
      <td>{b.connectionName || <span class="muted">—</span>}</td>
      <td>
        <Show when={b.kind === 'scheduled'} fallback={<span class="muted">Manual</span>}>
          <Show when={child} fallback={<><span class="badge badge-scheduled" title="Created by a scheduled backup job">scheduled</span><span class="mono" style="margin-left:6px;">{b.jobName || <span class="muted">(unnamed job)</span>}</span></>}>
            <span class="muted" style="font-size:11px;">part of run above</span>
          </Show>
        </Show>
      </td>
      <td>{b.note || <span class="muted">—</span>}</td>
      <td class="mono">{formatBytes(b.sizeBytes || 0)}</td>
      <td class="muted mono" style="font-size:12px;">{fmtTime(b.createdAt)}</td>
      <Show when={isAdmin()}><td class="muted">{b.createdByName}</td></Show>
      <td><span classList={{ 'status-ok': b.status === 'ok', 'status-err': b.status === 'error' }}>{b.status}</span></td>
      <td class="bk-actions" onClick={(e) => e.stopPropagation()}>
        <button class="btn btn-sm btn-ghost" title="Download this backup file" onClick={() => downloadServerBackup(b.id, b.filename)}>⤓</button>
      </td>
    </tr>
  );

  // The parent row for a scheduled run + (when open) its per-database children.
  const GroupRowTr = (e) => {
    const open = () => expanded().has(e.runId);
    return (
      <>
        <tr class="bk-group-row" style="cursor:pointer;" onClick={() => toggleExpand(e.runId)}
            title="Scheduled backup run — click to show the databases backed up in this run">
          <td class="bk-ck" onClick={(ev) => ev.stopPropagation()}>
            <input type="checkbox" title="Select all backups in this run"
                   checked={e.items.length > 0 && e.items.every((x) => isSel(x.id))}
                   onChange={(ev) => setSelMany(e.items.map((x) => x.id), ev.currentTarget.checked)} />
          </td>
          <td><span class="bk-chevron">{open() ? '▾' : '▸'}</span> <span class="mono">{e.database}</span></td>
          <td>{e.connectionName || <span class="muted">—</span>}</td>
          <td><span class="badge badge-scheduled">scheduled</span><span class="mono" style="margin-left:6px;">{e.jobName || <span class="muted">(unnamed job)</span>}</span></td>
          <td class="muted">{e.items.length} backup{e.items.length === 1 ? '' : 's'} in this run</td>
          <td class="mono">{formatBytes(e.sizeBytes || 0)}</td>
          <td class="muted mono" style="font-size:12px;">{fmtTime(e.createdAt)}</td>
          <Show when={isAdmin()}><td class="muted">{e.createdByName}</td></Show>
          <td><span classList={{ 'status-ok': e.status === 'ok', 'status-err': e.status === 'error', 'status-warn': e.status === 'partial' }}>{e.status}</span></td>
          <td class="bk-actions" />
        </tr>
        <Show when={open()}>
          <For each={e.items}>{(b) => BackupRowTr(b, true)}</For>
        </Show>
      </>
    );
  };

  // Sortable header cell.
  const Th = (p) => (
    <th style="cursor:pointer;user-select:none;white-space:nowrap;"
        title="Click to sort" onClick={() => toggleSort(p.k)}>
      {p.label}<span class="sort-ind">{sortKey() === p.k ? (sortDir() === 'asc' ? ' ▲' : ' ▼') : ''}</span>
    </th>
  );

  // ── Active operations ──────────────────────────────────────────────────────
  // Poll the in-flight backup/restore jobs and render them in a table (with an
  // inline progress bar per row) instead of a modal. Done ops drop out (the
  // finished backup shows in the list below); error/aborted stay until
  // dismissed. A paused restore exposes inline Skip / Skip-all / Abort.
  const [ops, setOps] = createSignal([]);
  const [dismissed, setDismissed] = createSignal(new Set()); // tokens hidden by the user
  let prevPhase = {}; // token → last phase seen (to detect done transitions)
  let pollTimer;

  const pollOps = async () => {
    let list = [];
    try { list = await listServerOperations(); } catch { return; }
    // Detect terminal transitions for toasts + backups refetch.
    let backupDone = false;
    for (const op of list) {
      const was = prevPhase[op.token];
      if (op.phase === 'done' && was && was !== 'done') {
        if (op.kind === 'backup') { toast.success(`Backup done — ${op.target}`); backupDone = true; }
        else toast.success(`Restore done — ${op.target}`);
      }
      if (op.phase === 'error' && was !== 'error') toast.error(`${op.kind} failed — ${op.target}`);
    }
    prevPhase = Object.fromEntries(list.map((o) => [o.token, o.phase]));
    setOps(list);
    if (backupDone) { refetch(); refetchStorage(); }
  };

  onMount(() => {
    pollOps();
    pollTimer = setInterval(pollOps, 1500);
    onCleanup(() => clearInterval(pollTimer));
  });

  // Rows to show: running / paused always; error / aborted until dismissed.
  // Successful (done) ops drop out — the finished backup appears in the list below.
  const visibleOps = () => ops().filter((o) =>
    o.phase !== 'done' && !dismissed().has(o.token));

  const opPct = (o) => (o.total > 0 ? Math.min(100, Math.round((o.current / o.total) * 100)) : null);
  const dismissOp = (tok) => setDismissed((s) => { const n = new Set(s); n.add(tok); return n; });
  const resolveOp = async (tok, action) => {
    try { await resolveRestore(tok, action); pollOps(); }
    catch (e) { toast.error('Resolve failed: ' + (e?.message || e)); }
  };

  // Flow modals.
  const [newPick, setNewPick]         = createSignal(null);   // { easy } — show conn/db picker for a new backup
  const [newDialog, setNewDialog]     = createSignal(null);   // { connectionId, connectionName, database, easy }
  const [restorePick, setRestorePick] = createSignal(null);   // { backup, easy } — show target picker
  const [restoreDialog, setRestoreDialog] = createSignal(null); // { backup, target…, easy }

  const startNew = (e) => setNewPick({ easy: userEasy() || !!e?.shiftKey });
  const startRestore = (e) => {
    const b = selectedOne();
    if (!b) return;
    setRestorePick({ backup: b, easy: userEasy() || !!e?.shiftKey });
  };

  const onDelete = async () => {
    const recs = selectedRecords();
    if (recs.length === 0) return;
    const msg = recs.length === 1
      ? `Delete backup "${recs[0].filename}"?\n\nThe stored dump file is removed. This cannot be undone.`
      : `Delete ${recs.length} selected backups?\n\nTheir stored dump files are removed. This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await bulkDeleteServerBackups(recs.map((b) => b.id));
      toast.success(`Deleted ${r.deleted} backup(s) — freed ${formatBytes(r.freedBytes || 0)}.`);
      clearSel();
      refetch();
      refetchStorage();
    } catch (e) {
      toast.error('Delete failed: ' + (e?.message || e));
    }
  };

  // ── Prune older than N days (admin) ─────────────────────────────────────────
  // Deletes backups whose Taken date is more than N days ago, WITHIN the tab's
  // active filters (displayed() already applies them). The client computes the id
  // set so "respect current filters" reuses the exact filtering the user sees.
  const [pruneOpen, setPruneOpen] = createSignal(false);
  const [pruneDays, setPruneDays]   = createSignal(30);
  const [pruneBusy, setPruneBusy]   = createSignal(false);
  // Whole days: the server's olderThanDays is an int (a decimal fails the whole
  // request), and the cutoff shown/applied here must match what's sent.
  const wholeDays = () => Math.round(Number(pruneDays()));
  const validDays = () => wholeDays() >= 1;
  const pruneTargets = () => {
    if (!validDays()) return [];
    const cutoff = Math.floor(Date.now() / 1000) - wholeDays() * 86400;
    return displayed().filter((b) => (b.createdAt || 0) < cutoff);
  };
  const pruneBytes = () => pruneTargets().reduce((n, b) => n + (b.sizeBytes || 0), 0);
  const scopeText = () => {
    const parts = [];
    if (fltConn()) parts.push(`connection “${fltConn()}”`);
    if (fltDb()) parts.push(`database “${fltDb()}”`);
    if (fltSource() === MANUAL) parts.push('manual backups');
    else if (fltSource()) parts.push(`schedule “${fltSource()}”`);
    if (fltUser()) parts.push(`user “${fltUser()}”`);
    if (search().trim()) parts.push(`search “${search().trim()}”`);
    return parts.length ? parts.join(', ') : 'all backups';
  };
  const doPrune = async () => {
    const ids = pruneTargets().map((b) => b.id);
    if (ids.length === 0) return;
    setPruneBusy(true);
    try {
      const r = await pruneServerBackups(ids, wholeDays());
      toast.success(`Pruned ${r.deleted} backup(s) — freed ${formatBytes(r.freedBytes || 0)}.`);
      setSelMany(ids, false);
      setPruneOpen(false);
      refetch(); refetchStorage();
    } catch (e) {
      toast.error('Prune failed: ' + (e?.message || e));
    } finally {
      setPruneBusy(false);
    }
  };

  return (
    <div class="page">
      <header class="page-header">
        <div>
          <h1>Backup / Restore</h1>
          <p class="muted">Take, restore, and manage server-side database backups.</p>
        </div>
        <div class="row-actions">
          <Show when={selectedIds().size > 0}>
            <span class="muted bk-selcount">{selectedIds().size} selected</span>
            <Show when={selectedOne()} fallback={
              <button class="btn" disabled title="Restore acts on a single backup — select just one (bulk restore isn't supported).">⬆ Restore</button>
            }>
              <button class="btn" onClick={startRestore} title="Restore the selected backup">⬆ Restore</button>
            </Show>
            <Show when={canDelete()}>
              <button class="btn btn-danger" onClick={onDelete}>🗑 Delete {selectedIds().size}</button>
            </Show>
            <button class="btn btn-ghost btn-sm" onClick={clearSel}>Clear</button>
          </Show>
          <Show when={isAdmin()}>
            <button class="btn btn-danger" onClick={() => setPruneOpen(true)}
                    title="Delete backups older than N days (respects the filters currently applied)">🧹 Prune old…</button>
          </Show>
          <button class="btn btn-primary" onClick={startNew}>+ New backup</button>
        </div>
      </header>

      {/* Storage usage — admin: backup-store folder size + disk free/%; a
          non-admin backup operator: only the size of their own backups. */}
      <Show when={storage()}>
        <div class="bk-storage">
          <Show
            when={storage().isAdmin}
            fallback={<span>Your backups: <strong>{gb(storage().ownSizeBytes)} GB</strong></span>}
          >
            <span>Backup store: <strong>{gb(storage().storeSizeBytes)} GB</strong></span>
            <Show when={storage().diskTotalBytes}>
              <span class="bk-storage-sep">·</span>
              <span>
                Disk free: <strong>{gb(storage().diskFreeBytes)} GB</strong> of {gb(storage().diskTotalBytes)} GB
                {' '}(<strong classList={{ 'bk-low': storage().diskFreePercent < 10 }}>{storage().diskFreePercent.toFixed(1)}%</strong> free)
              </span>
              <span class="bk-storage-bar" title={`${storage().diskFreePercent.toFixed(1)}% free`}>
                <span class="bk-storage-bar-used" classList={{ 'bk-low': storage().diskFreePercent < 10 }}
                      style={`width:${Math.min(100, Math.max(0, 100 - storage().diskFreePercent)).toFixed(1)}%`} />
              </span>
            </Show>
          </Show>
        </div>
      </Show>

      {/* Active operations — in-flight backups/restores with a live progress
          bar; a paused restore shows inline skip / skip-all / abort. */}
      <Show when={visibleOps().length > 0}>
        <div class="op-board">
          <div class="op-board-title">Active operations</div>
          <For each={visibleOps()}>
            {(o) => (
              <div class="op-row">
                <div class="op-row-head">
                  <span class={`op-kind op-kind-${o.kind}`}>{o.kind}</span>
                  <span class="op-target mono">{o.target}</span>
                  <span class="spacer" />
                  <span class="op-status" classList={{ 'status-err': o.phase === 'error' || o.phase === 'aborted' }}>
                    {o.phase === 'running' && o.kind === 'backup' ? `${o.current}/${o.total || '?'} tables`
                      : o.phase === 'running' ? `${o.current}/${o.total || '?'} statements`
                      : o.phase}
                    <Show when={o.skipped > 0}> · {o.skipped} skipped</Show>
                  </span>
                  <Show when={TERMINAL[o.phase]}>
                    <button class="btn btn-ghost btn-sm" title="Dismiss" onClick={() => dismissOp(o.token)}>✕</button>
                  </Show>
                </div>
                <div class="progress-track op-progress">
                  <div class={`progress-fill ${opPct(o) === null && o.phase === 'running' ? 'indeterminate' : ''} phase-${o.phase}`}
                       style={opPct(o) === null ? '' : `width:${opPct(o)}%`} />
                </div>
                <Show when={o.label && !TERMINAL[o.phase]}>
                  <div class="op-label mono">{o.label}</div>
                </Show>
                {/* Paused restore → resolve inline. */}
                <Show when={o.needsDecision}>
                  <div class="op-error">
                    <div class="op-error-msg mono">{o.error}</div>
                    <div class="op-decision">
                      <button class="btn btn-sm" onClick={() => resolveOp(o.token, 'skip')}>Skip this one</button>
                      <button class="btn btn-sm" onClick={() => resolveOp(o.token, 'skip-all')}>Skip all errors</button>
                      <button class="btn btn-sm btn-danger" onClick={() => resolveOp(o.token, 'abort')}>Abort</button>
                    </div>
                  </div>
                </Show>
                <Show when={(o.phase === 'error' || o.phase === 'aborted') && o.error && !o.needsDecision}>
                  <div class="op-error-msg mono">{o.error}</div>
                </Show>
              </div>
            )}
          </For>
        </div>
      </Show>

      <Show when={backups()} fallback={<div class="muted">Loading…</div>}>
        <Show when={(backups() || []).length > 0}
              fallback={<div class="muted" style="padding:16px;">No backups yet. Click <b>New backup</b> to take one.</div>}>
          {/* Search + filters — all client-side, on the fly. */}
          <div class="backup-filter-bar" style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:8px;">
            <input class="input" style="max-width:240px;" placeholder="Search database / connection / user / note…"
                   value={search()} onInput={(e) => setSearch(e.currentTarget.value)} />
            <select class="input" style="max-width:180px;" value={fltDb()} onChange={(e) => setFltDb(e.currentTarget.value)}>
              <option value="">All databases</option>
              <For each={dbOptions()}>{(v) => <option value={v}>{v}</option>}</For>
            </select>
            <select class="input" style="max-width:180px;" value={fltConn()} onChange={(e) => setFltConn(e.currentTarget.value)}>
              <option value="">All connections</option>
              <For each={connOptions()}>{(v) => <option value={v}>{v}</option>}</For>
            </select>
            <select class="input" style="max-width:200px;" value={fltSource()} onChange={(e) => setFltSource(e.currentTarget.value)} title="Filter by source: manual or a scheduled job">
              <option value="">All sources</option>
              <option value={MANUAL}>Manual (user-initiated)</option>
              <For each={scheduleOptions()}>{(v) => <option value={v}>{v}</option>}</For>
            </select>
            <Show when={isAdmin()}>
              <select class="input" style="max-width:160px;" value={fltUser()} onChange={(e) => setFltUser(e.currentTarget.value)}>
                <option value="">All users</option>
                <For each={userOptions()}>{(v) => <option value={v}>{v}</option>}</For>
              </select>
            </Show>
            <Show when={filtersActive()}>
              <button class="btn btn-sm btn-ghost" onClick={clearFilters}>Clear</button>
            </Show>
            <span class="spacer" />
            <span class="muted" style="font-size:12px;">{displayed().length} of {(backups() || []).length}</span>
          </div>
          <div class="data-table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="bk-ck">
                    <input type="checkbox" title="Select / clear all shown backups"
                           checked={displayed().length > 0 && displayed().every((b) => isSel(b.id))}
                           onChange={(e) => setSelMany(displayed().map((b) => b.id), e.currentTarget.checked)} />
                  </th>
                  <Th label="Database" k="database" />
                  <Th label="Connection" k="connectionName" />
                  <Th label="Source" k="jobName" />
                  <Th label="Note" k="note" />
                  <Th label="Size" k="sizeBytes" />
                  <Th label="Taken" k="createdAt" />
                  <Show when={isAdmin()}><Th label="By" k="createdByName" /></Show>
                  <Th label="Status" k="status" />
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <For each={grouped()}>
                  {(e) => (e.type === 'group' ? GroupRowTr(e) : BackupRowTr(e.backup, false))}
                </For>
              </tbody>
            </table>
          </div>
          <Show when={displayed().length === 0}>
            <div class="muted" style="padding:12px;">No backups match your search/filters. <button class="btn btn-sm btn-ghost" onClick={clearFilters}>Clear</button></div>
          </Show>
        </Show>
      </Show>

      {/* New backup: pick connection+database → BackupDialog (server mode). */}
      <Show when={newPick()}>
        <ConnDbPickerModal
          title="New backup — choose what to back up"
          confirmLabel="Continue"
          allowNew={false}
          onPick={(sel) => {
            setNewDialog({ connectionId: sel.connectionId, connectionName: sel.connectionName, database: sel.database, easy: newPick().easy });
            setNewPick(null);
          }}
          onClose={() => setNewPick(null)}
        />
      </Show>
      <Show when={newDialog()}>
        <BackupDialog
          serverMode={true}
          easy={newDialog().easy}
          connectionId={newDialog().connectionId}
          connectionName={newDialog().connectionName}
          database={newDialog().database}
          onStarted={pollOps}
          onClose={() => setNewDialog(null)}
        />
      </Show>

      {/* Restore: pick target connection+database → ServerRestoreDialog. */}
      <Show when={restorePick()}>
        <ConnDbPickerModal
          title="Restore into — choose the target"
          confirmLabel="Continue"
          allowNew={!restorePick().easy}
          onPick={(sel) => {
            setRestoreDialog({
              backup: restorePick().backup,
              connectionId: sel.connectionId, connectionName: sel.connectionName,
              database: sel.database, createDb: sel.createDb, easy: restorePick().easy,
            });
            setRestorePick(null);
          }}
          onClose={() => setRestorePick(null)}
        />
      </Show>
      {/* Prune old backups (admin) — older than N days, within the active filters. */}
      <Show when={pruneOpen()}>
        <Modal open={true} onClose={() => setPruneOpen(false)} title="Prune old backups">
          <p class="muted" style="font-size:13px;margin:0 0 12px;">
            Delete backups older than a cutoff to free space. This permanently removes the dump files — it cannot be undone.
          </p>
          <div class="settings-row">
            <label>Delete older than</label>
            <div style="display:flex;align-items:center;gap:8px;">
              <input class="input settings-num" type="number" min="1" value={pruneDays()}
                     onInput={(e) => setPruneDays(e.currentTarget.value)} />
              <span class="muted">days</span>
            </div>
          </div>
          <div class="settings-row">
            <label>Scope</label>
            <span style="font-size:13px;">{scopeText()}</span>
          </div>
          <div style="margin-top:8px;padding:10px 12px;border-radius:6px;background:var(--bg-elev-1);font-size:13px;">
            <Show when={validDays()} fallback={<span class="muted">Enter a number of days (1 or more).</span>}>
              <Show when={pruneTargets().length > 0}
                    fallback={<span class="muted">No backups are older than {wholeDays()} days in this scope.</span>}>
                <strong>{pruneTargets().length}</strong> backup(s) older than <strong>{wholeDays()}</strong> day(s)
                {' '}will be deleted, freeing <strong>{formatBytes(pruneBytes())}</strong>.
              </Show>
            </Show>
          </div>
          <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:14px;">
            <button class="btn btn-ghost" onClick={() => setPruneOpen(false)}>Cancel</button>
            <button class="btn btn-danger" disabled={pruneBusy() || pruneTargets().length === 0} onClick={doPrune}>
              {pruneBusy() ? 'Pruning…' : `Delete ${pruneTargets().length} backup(s)`}
            </button>
          </div>
        </Modal>
      </Show>

      <Show when={restoreDialog()}>
        <ServerRestoreDialog
          backup={restoreDialog().backup}
          connectionId={restoreDialog().connectionId}
          connectionName={restoreDialog().connectionName}
          database={restoreDialog().database}
          createDb={restoreDialog().createDb}
          easy={restoreDialog().easy}
          onDone={pollOps}
          onClose={() => setRestoreDialog(null)}
        />
      </Show>
    </div>
  );
}
